import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../../services/api";
import {
  cancelSubscription,
  undoCancellation,
  getInvoices,
  getPaymentMethod,
} from "../../services/paymentService";
import ConfirmDialog from "../../components/ConfirmDialog/ConfirmDialog";
import DataTable from "../../components/DataTable/DataTable";
import UsageMeter from "../../components/ui/UsageMeter/UsageMeter";
import { Button } from "../../components/ui";
import {
  resolveBillingMode,
  isSelfServiceBilling,
  resolveBillingModeNotice,
} from "../../utils/planCapabilities";
import styles from "./SubscriptionManagement.module.css";

const PLANS_ROUTE = "/subscription/plans";

const PERIOD_LABEL = {
  MONTHLY: "Mensal",
  ANNUAL: "Anual",
  SEMESTER: "Semestral",
};

// Badge humanizado do status da assinatura (NUNCA exibir o valor cru "ACTIVE").
// Classes mapeiam para tokens semânticos no CSS Module (success/primary/warning/neutro).
const STATUS_BADGE = {
  ACTIVE: { label: "Ativo", className: "statusActive" },
  TRIAL: { label: "Teste", className: "statusTrial" },
  OVERDUE: { label: "Inadimplente", className: "statusOverdue" },
  PENDING_CANCELLATION: {
    label: "Cancelamento agendado",
    className: "statusPendingCancellation",
  },
  CANCELED: { label: "Cancelado", className: "statusCanceled" },
};

// Rótulos humanizados dos status de fatura do Asaas (fallback: valor cru).
const INVOICE_STATUS_LABEL = {
  CONFIRMED: "Pago",
  RECEIVED: "Pago",
  RECEIVED_IN_CASH: "Pago",
  PENDING: "Pendente",
  AWAITING_RISK_ANALYSIS: "Em análise",
  OVERDUE: "Atrasado",
  REFUNDED: "Estornado",
  REFUND_REQUESTED: "Estorno solicitado",
  CHARGEBACK_REQUESTED: "Contestado",
};

function formatBRL(value) {
  if (value == null || Number.isNaN(Number(value))) return "—";
  return Number(value).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
}

function formatDatePtBr(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("pt-BR").format(date);
}

// Descreve o método de pagamento sem NUNCA expor o número completo do cartão.
function formatPaymentMethod(pm) {
  if (!pm) return "Não informado";
  const { billingType, brand, last4 } = pm;
  if ((billingType === "CREDIT_CARD" || last4) && last4) {
    return brand ? `Cartão •••• ${last4} (${brand})` : `Cartão •••• ${last4}`;
  }
  if (billingType === "BOLETO") return "Boleto";
  if (billingType === "PIX") return "Pix";
  // UNDEFINED / boleto/pix (sem cartão tokenizado).
  return "Boleto/Pix";
}

/**
 * Tela de GESTÃO da assinatura ("Plano e cobrança"). Renderiza DENTRO do
 * AppLayout (a sidebar global vem do layout) — aqui só o conteúdo + page-head.
 *
 * Dados reais:
 *  • GET /api/tenants/me/plan-usage → plano, limites, uso, status, trial,
 *    cancelAt, pendingPlanId, pendingPlanEffectiveDate.
 *  • GET /api/tenants/me            → preço, ciclo (period), próxima cobrança.
 *  • GET /api/payments/payment-method → método de pagamento (read-only).
 *  • GET /api/payments/invoices       → histórico de faturas (read-only).
 *
 * Ações reais (ADR-0019): cancelar (agenda → PENDING_CANCELLATION), desfazer
 * cancelamento (→ ACTIVE). A troca de plano vive na grade (/subscription/plans).
 */
export default function SubscriptionManagement() {
  const navigate = useNavigate();
  const [status, setStatus] = useState("loading"); // loading | ready | error
  const [planUsage, setPlanUsage] = useState(null);
  const [me, setMe] = useState(null);

  // Método de pagamento e faturas: seções independentes com estado próprio para
  // que uma falha nelas não derrube a página inteira.
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [paymentMethodStatus, setPaymentMethodStatus] = useState("loading");
  const [invoices, setInvoices] = useState([]);
  const [invoicesStatus, setInvoicesStatus] = useState("loading");

  // Cancelamento (real → agenda) e desfazer.
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelDetail, setCancelDetail] = useState("");
  const [canceling, setCanceling] = useState(false);
  const [undoing, setUndoing] = useState(false);

  // Devolve o `billingMode` já resolvido para que `loadBillingDetails` saiba se
  // pode chamar o gateway — o modo só é conhecido DEPOIS deste fetch, e ler o
  // state logo em seguida veria o valor do render anterior.
  const loadData = useCallback(async () => {
    setStatus("loading");
    try {
      const [usageRes, meRes] = await Promise.all([
        api.get("/tenants/me/plan-usage"),
        api.get("/tenants/me"),
      ]);
      setPlanUsage(usageRes.data);
      setMe(meRes.data);
      setStatus("ready");
      // Mesma precedência do resto da tela: `plan-usage` primeiro, `me` depois.
      return resolveBillingMode({
        billingMode: usageRes.data?.billingMode ?? meRes.data?.tenant?.billingMode,
      });
    } catch {
      setStatus("error");
      return null;
    }
  }, []);

  const loadBillingDetails = useCallback((billingMode) => {
    // ADR-0034 §4: `MANUAL`/`INTERNAL` nunca tocam o Asaas. O backend já responde
    // `[]` e `{ billingType: null }` sem chamar o gateway, mas a requisição em si
    // não tem para onde ir na UI — as duas seções ficam ocultas. Marcamos os
    // estados como "ready" (e não os deixamos em "loading") porque um skeleton de
    // seção que nunca renderiza jamais sairia do ar se algo voltasse a lê-lo.
    if (!isSelfServiceBilling(billingMode)) {
      setPaymentMethod(null);
      setPaymentMethodStatus("ready");
      setInvoices([]);
      setInvoicesStatus("ready");
      return;
    }

    setPaymentMethodStatus("loading");
    getPaymentMethod()
      .then((res) => {
        setPaymentMethod(res.data);
        setPaymentMethodStatus("ready");
      })
      .catch(() => setPaymentMethodStatus("error"));

    setInvoicesStatus("loading");
    getInvoices()
      .then((res) => {
        const data = res.data;
        setInvoices(Array.isArray(data) ? data : (data?.invoices ?? []));
        setInvoicesStatus("ready");
      })
      .catch(() => setInvoicesStatus("error"));
  }, []);

  // Carga completa: o modo sai de `loadData` e decide se as duas chamadas
  // financeiras acontecem. É também o handler do "Tentar novamente".
  const loadAll = useCallback(async () => {
    const billingMode = await loadData();
    loadBillingDetails(billingMode);
  }, [loadData, loadBillingDetails]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // ── Cancelar (REAL): agenda o cancelamento para o fim do período pago ──────
  const handleConfirmCancel = async () => {
    setCanceling(true);
    try {
      const detail = cancelDetail.trim();
      const res = await cancelSubscription(detail ? { detail } : undefined);
      // O backend distingue os dois fluxos (ADR-0019): OVERDUE encerra na hora
      // (immediate/CANCELED), ACTIVE agenda (PENDING_CANCELLATION). O badge se
      // atualiza sozinho no reload; aqui só ajustamos a mensagem de sucesso.
      const immediate =
        res?.data?.immediate === true || res?.data?.status === "CANCELED";
      toast.success(
        immediate
          ? "Assinatura encerrada. Como o pagamento estava pendente, não havia período pago a manter."
          : "Cancelamento agendado. Seu acesso continua até o fim do período pago.",
      );
      setCancelOpen(false);
      setCancelDetail("");
      await loadData();
    } catch (err) {
      toast.error(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          "Não foi possível cancelar agora. Tente novamente.",
      );
    } finally {
      setCanceling(false);
    }
  };

  // ── Desfazer cancelamento (REAL): volta a assinatura para ACTIVE ───────────
  const handleUndoCancellation = async () => {
    setUndoing(true);
    try {
      await undoCancellation();
      toast.success("Cancelamento desfeito. Sua assinatura segue ativa.");
      await loadData();
    } catch (err) {
      toast.error(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          "Não foi possível desfazer o cancelamento. Tente novamente.",
      );
    } finally {
      setUndoing(false);
    }
  };

  // ── Loading ───────────────────────────────────────────────────────────────
  if (status === "loading") {
    return (
      <div className={styles.page}>
        <header className={styles.pageHead}>
          <h1 className={styles.pageTitle}>Plano e cobrança</h1>
        </header>
        <div className={styles.card} aria-busy="true">
          <div className={styles.skeletonLine} style={{ width: "40%" }} />
          <div className={styles.skeletonLine} style={{ width: "70%" }} />
          <div className={styles.skeletonLine} style={{ width: "55%" }} />
          <p className={styles.loadingText}>Carregando plano e cobrança…</p>
        </div>
      </div>
    );
  }

  // ── Erro ──────────────────────────────────────────────────────────────────
  if (status === "error") {
    return (
      <div className={styles.page}>
        <header className={styles.pageHead}>
          <h1 className={styles.pageTitle}>Plano e cobrança</h1>
        </header>
        <div className={styles.card}>
          <p className={styles.errorText} role="alert">
            Não conseguimos carregar seu plano agora. Tente novamente em
            instantes.
          </p>
          <Button variant="primary" onClick={loadAll}>
            Tentar novamente
          </Button>
        </div>
      </div>
    );
  }

  // ── Dados derivados ───────────────────────────────────────────────────────
  const plan = me?.plan ?? {};
  const usagePlan = planUsage?.plan ?? {};
  const usage = planUsage?.usage ?? {};
  const subStatus =
    planUsage?.subscriptionStatus ?? me?.tenant?.subscriptionStatus;
  const badge = STATUS_BADGE[subStatus];
  const planName = usagePlan.name ?? plan.name ?? "—";
  const periodLabel = PERIOD_LABEL[plan.period] ?? "—";
  const nextBilling = formatDatePtBr(me?.tenant?.nextBillingDate);
  const trialEnd = formatDatePtBr(planUsage?.trialExpiration);
  const cancelAt = formatDatePtBr(
    planUsage?.cancelAt ?? me?.tenant?.cancelAt,
  );
  const pendingPlanId = planUsage?.pendingPlanId ?? me?.tenant?.pendingPlanId;
  const pendingPlanEffective = formatDatePtBr(
    planUsage?.pendingPlanEffectiveDate ??
      me?.tenant?.pendingPlanEffectiveDate,
  );
  const isCanceled = subStatus === "CANCELED";
  const isPendingCancel = subStatus === "PENDING_CANCELLATION";

  // Autoridade financeira (ADR-0034). Mesma precedência dos demais campos:
  // `plan-usage` (raiz) primeiro, `me.tenant` depois. Fail-safe `ASAAS` mora no
  // `resolveBillingMode` — aqui ninguém compara a string solta.
  const billingMode = resolveBillingMode({
    billingMode: planUsage?.billingMode ?? me?.tenant?.billingMode,
  });
  const selfServiceBilling = isSelfServiceBilling(billingMode);
  const billingModeNotice = resolveBillingModeNotice(billingMode);

  // Linhas da tabela de faturas (células podem ser nós React — ver DataTable).
  const invoiceRows = invoices.map((inv, i) => [
    formatDatePtBr(inv.date),
    inv.description ?? "—",
    formatBRL(inv.value),
    INVOICE_STATUS_LABEL[inv.status] ?? inv.status ?? "—",
    inv.receiptUrl ? (
      <a
        key={`receipt-${i}`}
        href={inv.receiptUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={styles.dialogLink}
      >
        Ver comprovante
      </a>
    ) : (
      "—"
    ),
  ]);

  return (
    <div className={styles.page}>
      <header className={styles.pageHead}>
        <div>
          <h1 className={styles.pageTitle}>Plano e cobrança</h1>
          <p className={styles.pageSubtitle}>
            Acompanhe seu plano, o uso do ciclo e a cobrança da sua assinatura.
          </p>
        </div>
      </header>

      {/* ── Card: plano atual ────────────────────────────────────────────── */}
      <section className={styles.card} aria-labelledby="current-plan-title">
        <div className={styles.cardHead}>
          <div className={styles.planHeading}>
            <h2 id="current-plan-title" className={styles.cardTitle}>
              {planName}
            </h2>
            <span className={styles.cycle}>{periodLabel}</span>
          </div>
          {badge && (
            <span
              className={`${styles.badge} ${styles[badge.className]}`}
              role="status"
            >
              {badge.label}
            </span>
          )}
        </div>

        {/* Aviso de modo de cobrança (ADR-0034). Informativo, não bloqueio: o
            tenant TEM plano e limites reais — só não é cobrado por aqui. Por
            isso `role="status"`, sem cadeado e sem CTA de upgrade. */}
        {billingModeNotice && (
          <div className={styles.modeNotice} role="status">
            <p className={styles.modeNoticeTitle}>{billingModeNotice.title}</p>
            <p className={styles.modeNoticeText}>{billingModeNotice.text}</p>
          </div>
        )}

        {subStatus === "TRIAL" && (
          <p className={styles.trialInfo}>
            {trialEnd !== "—"
              ? `Seu período de teste encerra em ${trialEnd}.`
              : "Você está no período de teste."}
          </p>
        )}
        {subStatus === "OVERDUE" && (
          <p className={styles.overdueInfo} role="alert">
            Seu pagamento está em atraso. Regularize para manter o acesso.
          </p>
        )}

        {/* Desfazer cancelamento é operação de assinatura no Asaas — não existe
            para quem não é cobrado por ele. */}
        {selfServiceBilling && isPendingCancel && (
          <div className={styles.pendingCancelInfo} role="alert">
            <p className={styles.pendingCancelText}>
              {cancelAt !== "—"
                ? `Cancelamento agendado para ${cancelAt}. Seu acesso continua até lá.`
                : "Cancelamento agendado. Seu acesso continua até o fim do período pago."}
            </p>
            <Button
              variant="secondary"
              onClick={handleUndoCancellation}
              isLoading={undoing}
            >
              Desfazer cancelamento
            </Button>
          </div>
        )}

        {pendingPlanId && (
          <p className={styles.trialInfo}>
            {pendingPlanEffective !== "—"
              ? `Você tem uma troca de plano agendada para ${pendingPlanEffective}.`
              : "Você tem uma troca de plano agendada para o próximo ciclo."}
          </p>
        )}

        <dl className={styles.details}>
          <div className={styles.detailItem}>
            <dt className={styles.detailLabel}>Valor</dt>
            <dd className={styles.detailValue}>{formatBRL(plan.price)}</dd>
          </div>
          {/* "Próxima cobrança" e "Método de pagamento" só existem quando há
              cobrança: em MANUAL/INTERNAL o `nextBillingDate` é NULL por
              decisão (ADR-0034 §6) e não há método algum a exibir. */}
          {selfServiceBilling && (
            <>
              <div className={styles.detailItem}>
                <dt className={styles.detailLabel}>Próxima cobrança</dt>
                <dd className={styles.detailValue}>{nextBilling}</dd>
              </div>
              <div className={styles.detailItem}>
                <dt className={styles.detailLabel}>Método de pagamento</dt>
                <dd className={styles.detailValue}>
                  {paymentMethodStatus === "loading"
                    ? "Carregando…"
                    : paymentMethodStatus === "error"
                      ? "Não informado"
                      : formatPaymentMethod(paymentMethod)}
                </dd>
              </div>
            </>
          )}
        </dl>

        {/* Troca e cancelamento são self-service financeiro (ADR-0034 §7):
            mudança de plano manual/interna é operação administrativa. */}
        {selfServiceBilling && (
          <div className={styles.actions}>
            <Button variant="primary" onClick={() => navigate(PLANS_ROUTE)}>
              Trocar de plano
            </Button>
            {!isCanceled && !isPendingCancel && (
              <Button variant="ghost" onClick={() => setCancelOpen(true)}>
                Cancelar assinatura
              </Button>
            )}
          </div>
        )}
      </section>

      {/* ── Uso neste ciclo ──────────────────────────────────────────────── */}
      <section className={styles.card} aria-labelledby="usage-title">
        <h2 id="usage-title" className={styles.sectionTitle}>
          Uso neste ciclo
        </h2>
        <div className={styles.meters}>
          <UsageMeter
            label="Membros"
            used={usage.members?.used}
            limit={usage.members?.limit}
            percentage={usage.members?.percentage}
          />
          <UsageMeter
            label="Leads"
            used={usage.leads?.used}
            limit={usage.leads?.limit}
            percentage={usage.leads?.percentage}
          />
          <UsageMeter
            label="Mensagens"
            used={usage.messages?.used}
            limit={usage.messages?.limit}
            percentage={usage.messages?.percentage}
          />
        </div>
      </section>

      {/* ── Histórico de faturas (dados reais — GET /payments/invoices) ─────
          Só existe em ASAAS: para os demais modos o backend devolve `[]` sem
          tocar o gateway, e uma tabela vazia com "você ainda não tem faturas"
          insinuaria que faturas virão — o que é falso, não "ainda não". */}
      {selfServiceBilling && (
        <section className={styles.card} aria-labelledby="invoices-title">
          <h2 id="invoices-title" className={styles.sectionTitle}>
            Histórico de faturas
          </h2>
          {invoicesStatus === "loading" ? (
            <p className={styles.loadingText}>Carregando faturas…</p>
          ) : invoicesStatus === "error" ? (
            <p className={styles.errorText} role="alert">
              Não foi possível carregar suas faturas agora.
            </p>
          ) : (
            <DataTable
              headers={["Data", "Descrição", "Valor", "Status", ""]}
              rows={invoiceRows}
              emptyMsg="Você ainda não tem faturas."
            />
          )}
        </section>
      )}

      {/* ── Cancelamento: ação REAL (agenda para o fim do período pago) ───── */}
      <ConfirmDialog
        isOpen={cancelOpen}
        title="Cancelar assinatura"
        confirmLabel={canceling ? "Cancelando…" : "Confirmar cancelamento"}
        cancelLabel="Voltar"
        confirmVariant="danger"
        confirmDisabled={canceling}
        onConfirm={handleConfirmCancel}
        onCancel={() => {
          if (canceling) return;
          setCancelOpen(false);
        }}
      >
        {subStatus === "OVERDUE" ? (
          // Inadimplente: NÃO há período pago a manter — encerra imediatamente.
          // Nunca prometer "acesso continua até" nem reembolso proporcional.
          <p className={styles.dialogText}>
            Sua assinatura está com pagamento pendente. Ao cancelar, ela é
            encerrada imediatamente.
          </p>
        ) : (
          <p className={styles.dialogText}>
            {nextBilling !== "—"
              ? `Seu acesso continua até ${nextBilling} (fim do período já pago). Não há reembolso proporcional do tempo restante.`
              : "Seu acesso continua até o fim do período já pago. Não há reembolso proporcional do tempo restante."}
          </p>
        )}
        <label className={styles.dialogText} htmlFor="cancelDetail">
          Se quiser, conte pra gente o motivo (opcional):
        </label>
        <textarea
          id="cancelDetail"
          className={styles.reasonTextarea}
          rows={3}
          maxLength={500}
          placeholder="O que motivou seu cancelamento?"
          value={cancelDetail}
          onChange={(e) => setCancelDetail(e.target.value)}
          disabled={canceling}
        />
      </ConfirmDialog>
    </div>
  );
}
